import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useSEO from "@/lib/useSEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, ChevronLeft, ChevronRight, X, Camera, FileText, CheckCircle2 } from "lucide-react";

const PROJECT_TYPES = [
  { value: "installation", label: "Installation d'un spa" },
  { value: "deplacement", label: "Déplacement de spa" },
  { value: "remise_en_service", label: "Remise en service" },
  { value: "entretien", label: "Contrat d'entretien" },
  { value: "autre", label: "Autre projet" },
];

function QuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", project_type: "installation", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Indiquez votre nom.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Email invalide.");
    setSending(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      toast.success("Demande envoyée ! Nous vous recontactons sous 48h.");
    } catch {
      toast.error("Envoi impossible pour le moment. Réessayez plus tard.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-10" data-testid="quote-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
        <h3 className="mt-4 font-display text-2xl font-black tracking-tight">Demande bien reçue !</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Notre équipe étudie votre projet et vous recontacte sous 48h à <strong>{form.email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="quote-form">
      <div>
        <Label htmlFor="q-name" className="text-xs font-semibold">Nom complet *</Label>
        <Input id="q-name" value={form.name} onChange={set("name")} placeholder="Jean Dupont" required maxLength={200} className="mt-1.5 rounded-xl" data-testid="quote-name-input" />
      </div>
      <div>
        <Label htmlFor="q-email" className="text-xs font-semibold">Email *</Label>
        <Input id="q-email" type="email" value={form.email} onChange={set("email")} placeholder="jean@exemple.fr" required maxLength={320} className="mt-1.5 rounded-xl" data-testid="quote-email-input" />
      </div>
      <div>
        <Label htmlFor="q-phone" className="text-xs font-semibold">Téléphone</Label>
        <Input id="q-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" maxLength={40} className="mt-1.5 rounded-xl" data-testid="quote-phone-input" />
      </div>
      <div>
        <Label htmlFor="q-city" className="text-xs font-semibold">Ville / Code postal</Label>
        <Input id="q-city" value={form.city} onChange={set("city")} placeholder="Annecy (74)" maxLength={120} className="mt-1.5 rounded-xl" data-testid="quote-city-input" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="q-type" className="text-xs font-semibold">Type de projet</Label>
        <select
          id="q-type"
          value={form.project_type}
          onChange={set("project_type")}
          className="mt-1.5 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="quote-type-select"
        >
          {PROJECT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="q-message" className="text-xs font-semibold">Votre projet</Label>
        <Textarea id="q-message" value={form.message} onChange={set("message")} placeholder="Décrivez votre projet : type de spa, emplacement (terrasse, jardin…), accès, délais souhaités…" maxLength={2000} rows={4} className="mt-1.5 rounded-xl" data-testid="quote-message-input" />
      </div>
      <div className="sm:col-span-2 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[11px] text-muted-foreground">* Champs obligatoires — réponse sous 48h ouvrées.</p>
        <Button type="submit" disabled={sending} className="rounded-full h-11 px-7 font-bold" data-testid="quote-submit-btn">
          {sending ? "Envoi en cours…" : "Demander mon devis gratuit"}
          {!sending && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </form>
  );
}

const PHOTOS = ["01", "02", "03", "04", "05", "06", "07", "09", "10", "11", "12", "14", "16", "17", "18"].map(
  (n) => `/hero/hero-${n}.jpg`
);

export default function Installations() {
  const [lightbox, setLightbox] = useState(null);

  useSEO({
    title: "Nos installations de spas — Galerie | DoctoSpa",
    description:
      "Découvrez nos réalisations : spas et jacuzzis installés en terrasse, jardin, montagne. De l'installation à l'entretien, DoctoSpa accompagne votre spa toute l'année.",
  });

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % PHOTOS.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + PHOTOS.length) % PHOTOS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="installations-page">
      <div className="eyebrow text-secondary flex items-center gap-2">
        <Camera className="h-3.5 w-3.5" /> Galerie
      </div>
      <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
        Nos <span className="text-primary">installations</span>
      </h1>
      <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">
        Terrasse, jardin, montagne ou bord de piscine : chaque spa a son histoire. Voici quelques-unes
        de nos réalisations — et si le vôtre fait des siennes, on le diagnostique en 2 minutes.
      </p>

      <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]" data-testid="installations-grid">
        {PHOTOS.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setLightbox(i)}
            className="group relative mb-4 block w-full overflow-hidden rounded-2xl border border-black/10 focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid={`installation-photo-${i}`}
          >
            <img
              src={src}
              alt={`Installation de spa DoctoSpa n°${i + 1}`}
              loading={i < 6 ? "eager" : "lazy"}
              className="w-full transition-transform duration-500 group-hover:scale-[1.04]"
            />
            <span className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/20 transition-colors" />
          </button>
        ))}
      </div>

      {/* DEVIS INSTALLATION */}
      <section id="devis" className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="quote-section">
        <div className="lg:col-span-4">
          <div className="eyebrow text-secondary flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Devis gratuit
          </div>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-black tracking-tight leading-tight">
            Un projet d'<span className="text-primary">installation</span> ?
          </h2>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            Installation, déplacement, remise en service ou entretien : décrivez votre projet
            et recevez un devis personnalisé sous 48h — sans engagement.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {["Étude gratuite de votre projet", "Intervention en Haute-Savoie et alentours", "Techniciens spécialistes spa & jacuzzi"].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <span className="text-muted-foreground">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-8 rounded-3xl border border-black/10 bg-stone-50 p-6 lg:p-10">
          <QuoteForm />
        </div>
      </section>

      {/* CTA */}
      <div className="mt-14 rounded-3xl bg-slate-950 text-white p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight">Votre spa fait des siennes ?</h2>
          <p className="mt-2 text-sm text-slate-300">
            Diagnostic gratuit en 2 minutes — causes probables, pièces à remplacer et kits prêts à monter.
          </p>
        </div>
        <Link to="/diagnostic" data-testid="installations-cta-diagnostic">
          <Button size="lg" className="rounded-full h-12 px-7 bg-white text-slate-950 hover:bg-slate-200 font-bold">
            Lancer mon diagnostic <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="installations-lightbox"
        >
          <button
            className="absolute top-5 right-5 text-white/70 hover:text-white"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
          >
            <X className="h-8 w-8" />
          </button>
          <button
            className="absolute left-3 lg:left-8 text-white/70 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + PHOTOS.length) % PHOTOS.length); }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <img
            src={PHOTOS[lightbox]}
            alt={`Installation de spa DoctoSpa n°${lightbox + 1}`}
            className="max-h-[88vh] max-w-[88vw] rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-3 lg:right-8 text-white/70 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % PHOTOS.length); }}
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-10 w-10" />
          </button>
          <span className="absolute bottom-5 text-white/60 text-xs font-mono">{lightbox + 1} / {PHOTOS.length}</span>
        </div>
      )}
    </div>
  );
}
